import en from './en';

// The interface is written in French; in English every text a component
// renders is looked up in the dictionary (en.js, keyed by the French text,
// whitespace collapsed), and a text absent from it stays as written. A key
// with {0}, {1}… is a template: the values in between are kept (and
// translated when they are themselves a known text or template).

let lang = 'fr';

export const setUILanguage = (language) => {
  lang = language === 'English' ? 'en' : 'fr';
  if (typeof document !== 'undefined') document.documentElement.lang = lang;
};

export const uiLanguage = () => lang;

// uiLocale formats numbers and dates in the interface's language.
export const uiLocale = () => (lang === 'en' ? 'en-GB' : 'fr-FR');

const norm = (s) => s.replace(/\s+/g, ' ').trim();
const escape = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const has = (key) => Object.prototype.hasOwnProperty.call(en, key);

const templates = Object.keys(en)
  .filter((key) => /\{\d+\}/.test(key))
  .map((key) => {
    const parts = key.split(/\{(\d+)\}/);
    const literals = parts.filter((_, i) => i % 2 === 0);
    const slots = parts.filter((_, i) => i % 2 === 1).map(Number);
    return {
      // A value glued to a word ("ordre{1}") is a plural ending, so "{0}
      // an{1}" never cuts "Un analyste"; other values are short (a name, a
      // date, an amount), so an AI paragraph is never taken for a template.
      re: new RegExp(`^${parts.map((p, i) => (i % 2 === 0 ? escape(p) : /\p{L}$/u.test(parts[i - 1]) ? '(\\p{L}{0,3})' : '([\\s\\S]{0,100}?)')).join('')}$`, 'u'),
      slots,
      longest: literals.reduce((a, b) => (b.length > a.length ? b : a), ''),
      weight: literals.join('').length,
      text: en[key],
    };
  })
  .sort((a, b) => b.weight - a.weight);

const cache = new Map();

// spaced puts the text's surrounding spaces back around its translation.
const spaced = (text, out) => text.match(/^\s*/)[0] + out + text.match(/\s*$/)[0];

const lookup = (key, exactOnly) => {
  if (has(key)) return en[key];
  if (exactOnly) return null;
  for (const t of templates) {
    if (t.longest && !key.includes(t.longest)) continue;
    const m = t.re.exec(key);
    if (!m) continue;
    const values = {};
    t.slots.forEach((slot, i) => {
      const v = m[i + 1];
      const t = v.trim() && lookup(norm(v));
      values[slot] = t ? spaced(v, t) : v;
    });
    return t.text.replace(/\{(\d+)\}/g, (_, slot) => values[slot] ?? '');
  }
  // "Pilote du panel · 14 Oct 2026": labels joined by code, one by one.
  if (key.includes(' · ')) {
    const parts = key.split(' · ');
    const out = parts.map((p) => (p && lookup(p)) || p);
    if (out.some((p, i) => p !== parts[i])) return out.join(' · ');
  }
  return null;
};

// tr translates one text (keeping its surrounding spaces).
export const tr = (text) => {
  if (lang !== 'en' || typeof text !== 'string') return text;
  const key = norm(text);
  if (!key) return text;
  // A text on several lines is written content (an AI report): exact only.
  const multiline = text.includes('\n');
  const ck = multiline ? `\n${key}` : key;
  let out = cache.get(ck);
  if (out === undefined) {
    out = lookup(key, multiline);
    if (cache.size > 5000) cache.clear();
    cache.set(ck, out);
  }
  if (out === null) return text;
  return spaced(text, out);
};

// The props whose strings are read by a person.
const TEXT_PROPS = ['title', 'name', 'description', 'label', 'placeholder', 'aria-label', 'ariaLabel', 'alt', 'hint', 'message', 'eyebrow', 'tip',
  'content', 'caption', 'empty', 'emptyText', 'emptyLabel', 'confirmLabel', 'cancelLabel', 'subtitle', 'heading', 'text', 'note', 'legend', 'unit', 'action'];

// translateProps returns the props with their texts in the interface's
// language (the same object in French).
export const translateProps = (props) => {
  if (lang !== 'en' || !props) return props;
  let out = props;
  const set = (key, value) => {
    if (value === props[key]) return;
    if (out === props) out = { ...props };
    out[key] = value;
  };
  const { children } = props;
  if (typeof children === 'string') set('children', tr(children));
  else if (Array.isArray(children) && children.some((c) => typeof c === 'string')) set('children', children.map((c) => (typeof c === 'string' ? tr(c) : c)));
  TEXT_PROPS.forEach((key) => {
    if (typeof props[key] === 'string') set(key, tr(props[key]));
  });
  return out;
};
